import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  List,
  ListDivider,
  ListItem,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import groupBy from "lodash/groupBy";
import { Fragment, memo, useCallback, useMemo } from "react";
import {
  AddressableAudience,
  MediaDataRoomVersionSwitch,
  SeedAudience,
} from "features/mediaDataRoom/components";
import {
  AdvertiserAudienceGeneratorHeader,
  AdvertiserAudienceGeneratorStep,
  AudienceGeneratorStepActionsWrapper,
  AudienceGeneratorStepSectionDescription,
  AudienceGeneratorStepSectionTitle,
  useAdvertiserAudienceGenerator,
} from "features/mediaDataRoom/containers/AdvertiserAudienceGenerator";
import { useMediaDataRoom } from "features/mediaDataRoom/contexts";
import { useDataRoomSnackbar } from "hooks";
import { useLookalikeAdvertiserAudienceGenerator } from "../../contexts/LookalikeAdvertiserAudienceGeneratorContext";
import {
  AudienceGeneratorReachSlider,
  AudienceQualityStatistics,
  ExcludeSeedAudienceCheckbox,
} from "..";

const LookalikeAdvertiserAudienceGeneratorConfigurationStep = memo(() => {
  const {
    selectedSeedOption,
    setSelectedSeedOption,
    seedOptions: { computeResults: seedOptions = [] },
    selectedAudienceStatistics,
    showExcludeSeedAudienceCheckbox,
  } = useLookalikeAdvertiserAudienceGenerator();
  const { enqueueSnackbar } = useDataRoomSnackbar();
  const {
    addressableAudienceSize,
    seedAudienceSize,
    computeState: { loading: isStatisticsComputationLoading },
  } = selectedAudienceStatistics!;
  const {
    supportedFeatures: { showAbsoluteValues },
  } = useMediaDataRoom();
  const { handleBackStep, handleNextStep, isFirstStep, cancelGeneration } =
    useAdvertiserAudienceGenerator();
  const shouldCancelGenerationOnBack = isFirstStep(
    AdvertiserAudienceGeneratorStep.CONFIGURATION
  );
  const groupedSeedOptions = useMemo(
    () => Object.entries(groupBy(seedOptions, "group")),
    [seedOptions]
  );
  const handleContinue = useCallback(() => {
    if (selectedSeedOption?.isEmpty) {
      enqueueSnackbar(
        `The selected seed audience ${selectedSeedOption.name} is too small to compute this lookalike audience`,
        {
          variant: "info",
        }
      );
      return;
    }
    handleNextStep();
  }, [
    selectedSeedOption?.isEmpty,
    selectedSeedOption?.name,
    handleNextStep,
    enqueueSnackbar,
  ]);
  return (
    <Fragment>
      <Grid container={true} sx={{ height: "100%" }}>
        <Grid xs={7}>
          <Box mb={3}>
            <AdvertiserAudienceGeneratorHeader />
          </Box>
          <Stack direction="column" spacing={2}>
            <Box>
              <AudienceGeneratorStepSectionTitle>
                1. AI Lookalike audience setup
              </AudienceGeneratorStepSectionTitle>
              <AudienceGeneratorStepSectionDescription>
                Choose the audience that will be used to seed your lookalike
                model.
              </AudienceGeneratorStepSectionDescription>
            </Box>
            <FormControl>
              <FormLabel>
                <MediaDataRoomVersionSwitch
                  ab="Advertiser audience"
                  v3="Seed audience"
                />
              </FormLabel>
              <Stack>
                <Select
                  onChange={(_, value) => {
                    const selectedSeedOption = seedOptions.find(
                      ({ id }) => id === value
                    );
                    setSelectedSeedOption(selectedSeedOption!);
                  }}
                  value={selectedSeedOption?.id || ""}
                >
                  {groupedSeedOptions.map(([group, options], index) => (
                    <Fragment key={group}>
                      {index !== 0 && <ListDivider role="none" />}
                      <List aria-labelledby={`select-group-${group}`}>
                        <ListItem id={`select-group-${group}`} sticky={true}>
                          <Typography level="body-xs">{group}</Typography>
                        </ListItem>
                        {options.map(({ id, name }) => (
                          <Option key={id} value={id}>
                            {name}
                          </Option>
                        ))}
                      </List>
                    </Fragment>
                  ))}
                </Select>
                {selectedSeedOption?.isEmpty && (
                  <Alert color="warning">
                    The selected seed audience {selectedSeedOption.name} is too
                    small to compute this lookalike audience.
                  </Alert>
                )}
              </Stack>
            </FormControl>
            {showAbsoluteValues && (
              <Stack direction="row" spacing={2}>
                <AddressableAudience
                  addressableAudience={addressableAudienceSize}
                  loading={isStatisticsComputationLoading}
                />
                <SeedAudience
                  loading={isStatisticsComputationLoading}
                  seedAudience={seedAudienceSize}
                />
              </Stack>
            )}
          </Stack>
          <Stack direction="column" mt={4} spacing={3}>
            <Box>
              <AudienceGeneratorStepSectionTitle>
                2. Define the Reach
              </AudienceGeneratorStepSectionTitle>
              <AudienceGeneratorStepSectionDescription>
                Set the precision and reach for your lookalike model
              </AudienceGeneratorStepSectionDescription>
            </Box>
            <AudienceGeneratorReachSlider />
            {showExcludeSeedAudienceCheckbox && <ExcludeSeedAudienceCheckbox />}
          </Stack>
        </Grid>
        <Grid display="flex" justifyContent="center" xs={1}>
          <Divider orientation="vertical" />
        </Grid>
        <Grid container={true} xs={4}>
          <AudienceQualityStatistics />
        </Grid>
      </Grid>
      <AudienceGeneratorStepActionsWrapper>
        <Button
          onClick={
            shouldCancelGenerationOnBack ? cancelGeneration : handleBackStep
          }
          sx={{ mr: 1 }}
        >
          {shouldCancelGenerationOnBack ? "Cancel" : "Back"}
        </Button>
        <Button
          color="primary"
          disabled={!selectedSeedOption?.id}
          onClick={handleContinue}
          variant="solid"
        >
          Continue
        </Button>
      </AudienceGeneratorStepActionsWrapper>
    </Fragment>
  );
});

LookalikeAdvertiserAudienceGeneratorConfigurationStep.displayName =
  "LookalikeAdvertiserAudienceGeneratorConfigurationStep";

export default LookalikeAdvertiserAudienceGeneratorConfigurationStep;
